import { Injectable } from '@angular/core';
import { InteractService } from './interact.service';
import { CustomFieldTypeEnum } from 'src/app/enum/custom-field-type.enum';
import {
  calculatePlacementValidity,
  PlacementElementType,
  PlacementValidationElement
} from './placement-validation-engine';

@Injectable({
  providedIn: 'root'
})
export class StampsValidatorService {

  constructor(private interactService: InteractService) { }

  public onAfterCheckStamps() {
    const signatures = document.getElementsByClassName('signature');
    const rubrics = document.getElementsByClassName('rubric');
    const textFields = document.getElementsByClassName(CustomFieldTypeEnum.TextField);
    const checkboxFields = document.getElementsByClassName(CustomFieldTypeEnum.CheckboxField);

    let customFields = Array.prototype.slice.apply(textFields);
    customFields = customFields.concat(Array.from(checkboxFields));

    let signaturesRubrics = Array.prototype.slice.apply(signatures);
    signaturesRubrics = signaturesRubrics.concat(Array.from(rubrics));
    signaturesRubrics = signaturesRubrics.concat(Array.from(textFields));
    signaturesRubrics = signaturesRubrics.concat(Array.from(checkboxFields));

    const dropzonesByPage = this.getDropzonesByPage();
    const validationElements = signaturesRubrics
      .map((element: Element) => this.createPlacementValidationElement(element))
      .filter((element): element is PlacementValidationElement => element !== null);

    const validityByKey = calculatePlacementValidity(
      validationElements,
      this.interactService.fileName,
      dropzonesByPage
    );

    for (let index = 0; index < signaturesRubrics.length; index++) {
      const currentElement = signaturesRubrics[index];
      const key = this.getElementKey(currentElement);
      if (!key || !validityByKey.has(key)) {
        continue;
      }

      currentElement.setAttribute('valid', validityByKey.get(key) ? 'true' : 'false');
    }

    this.setInvalidSignatures(signatures);
    this.setInvalidRubrics(rubrics);
    this.setInvalidCustomFields(customFields);
  }

  private createPlacementValidationElement(element: Element): PlacementValidationElement | null {
    const fileName = element.id;
    const signatoryId = element.getAttribute('data-id');
    const gId = element.getAttribute('data-gId') || element.getAttribute('data-gid');
    const dataType = element.getAttribute('data-type');

    if (!fileName || !signatoryId || !gId) {
      return null;
    }

    const page = this.resolveElementPage(dataType, fileName, signatoryId, gId);
    if (!page) {
      return null;
    }

    const type = dataType === 'signature'
      ? 'signature'
      : (dataType === 'rubric' ? 'rubric' : 'customField');

    return {
      key: this.buildElementKey(fileName, signatoryId, gId),
      fileName,
      page,
      type: type as PlacementElementType,
      rect: element.getBoundingClientRect() as DOMRect,
      height: (element as HTMLElement).clientHeight
    };
  }

  private resolveElementPage(dataType: string | null, fileName: string, signatoryId: string, gId: string): string | null {
    if (dataType === 'signature') {
      const signatoryStamp = this.interactService.signatoryStamps.find(
        (x) => x.signatoryId === signatoryId && x.fileName === fileName && x.gId == gId
      );
      return signatoryStamp?.signaturePositionPage || null;
    }

    if (dataType === 'rubric') {
      const rubricStamp = this.interactService.rubricStamps.find(
        (x) => x.signatoryId === signatoryId && x.fileName === fileName && x.gId == gId
      );
      return rubricStamp?.page || null;
    }

    const customField = this.interactService.customFields.find(
      (x) => x.signatoryId === signatoryId && x.fileName === fileName && x.gId == gId
    );
    return customField?.page || null;
  }

  private getDropzonesByPage(): Map<string, DOMRect> {
    const dropzonesByPage = new Map<string, DOMRect>();
    const dropzones = Array.from(document.getElementsByClassName('page dropzone'));

    for (let index = 0; index < dropzones.length; index++) {
      const dropzone = dropzones[index] as HTMLElement;
      const page = dropzone.getAttribute('data-page-number');
      if (!page) {
        continue;
      }

      dropzonesByPage.set(page, dropzone.getBoundingClientRect() as DOMRect);
    }

    return dropzonesByPage;
  }

  private getElementKey(element: Element): string | null {
    const fileName = element.id;
    const signatoryId = element.getAttribute('data-id');
    const gId = element.getAttribute('data-gId') || element.getAttribute('data-gid');

    if (!fileName || !signatoryId || !gId) {
      return null;
    }

    return this.buildElementKey(fileName, signatoryId, gId);
  }

  private buildElementKey(fileName: string, signatoryId: string, gId: string): string {
    return `${fileName}::${signatoryId}::${gId}`;
  }

  setInvalidSignatures(signatures: HTMLCollectionOf<Element>) {
    for (let index = 0; index < signatures.length; index++) {
      const element = signatures[index];
      const fileName = element.id;
      const signatoryId = element.getAttribute('data-id');
      const gId = element.getAttribute('data-gId') || element.getAttribute('data-gid');

      if (!signatoryId || !gId) {
        continue;
      }

      const stampSignatory = this.interactService.signatoryStamps.find(
        (x) => x.fileName === fileName && x.signatoryId === signatoryId && x.gId == gId);

      if (!stampSignatory) {
        continue;
      }

      if (element.getAttribute('valid') === 'false') {
        element.classList.add('cannot-drop');
        stampSignatory.invalid = true;
      } else {
        element.classList.remove('cannot-drop');
        stampSignatory.invalid = false;
      }

      if (element.getAttribute('out-document') === 'true') {
        stampSignatory.invalid = true;
      }
    }
    this.interactService.validStep = this.isValidStep();
  }

  setInvalidCustomFields(customFields: HTMLCollectionOf<Element>) {
    for (let index = 0; index < customFields.length; index++) {
      const element = customFields[index];
      const fileName = element.id;
      const signatoryId = element.getAttribute('data-id');
      const gId = element.getAttribute('data-gId') || element.getAttribute('data-gid');

      if (!signatoryId || !gId) {
        continue;
      }

      const customField = this.interactService.customFields.find(
        (x) => x.fileName === fileName && x.signatoryId === signatoryId && x.gId == gId
      );

      if (!customField) {
        continue;
      }

      if (element.getAttribute('valid') === 'false') {
        element.classList.add('cannot-drop');
        customField.invalid = true;
      } else {
        element.classList.remove('cannot-drop');
        customField.invalid = false;
      }

      if (element.getAttribute('out-document') === 'true') {
        customField.invalid = true;
      }
    }

    this.interactService.validStep = this.isValidStep();
  }

  setInvalidRubrics(rubrics: HTMLCollectionOf<Element>) {
    for (let index = 0; index < rubrics.length; index++) {
      const element = rubrics[index];
      const fileName = element.id;
      const signatoryId = element.getAttribute('data-id');
      const gId = element.getAttribute('data-gId') || element.getAttribute('data-gid');

      if (!signatoryId || !gId) {
        continue;
      }

      const rubricSignatory = this.interactService.rubricStamps.find(
        (x) => x.fileName === fileName && x.signatoryId === signatoryId && x.gId === gId);

      if (rubricSignatory === undefined) {
        element.setAttribute('valid', 'false');
        element.classList.add('cannot-drop-out-document');
        continue;
      }

      if (element.getAttribute('valid') === 'false') {
        element.classList.add('cannot-drop-out-document');
        rubricSignatory.invalid = true;
      } else if (element.getAttribute('out-document') === 'true') {
        rubricSignatory.invalid = true;
      } else {
        element.classList.remove('cannot-drop-out-document');
        rubricSignatory.invalid = false;
        element.setAttribute('valid', 'true');
      }
    }
    this.interactService.validStep = this.isValidStep();
  }

  isValidStep() {
    return this.interactService.signatoryStamps.every((x) => x.invalid === false)
      && this.interactService.rubricStamps.every((x) => x.invalid === false)
      && this.interactService.customFields.every((x) => x.invalid === false);
  }
}
